import { useEffect } from "react";

const SITE_NAME = "OBRA Creatives";
const SITE_URL = "https://www.obracreatives.co.nz";
const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1765344550212-a3b963d63c32?w=1200&h=630&fit=crop";
const DEFAULT_TITLE = "OBRA Creatives | Photographer & Videographer in Manawatū & Wellington";
const DEFAULT_DESC = "Commercial photographer and videographer serving Manawatū, Palmerston North and Wellington. Brand photography, video production, social media content and campaigns by OBRA Creatives.";
const KEYWORDS = "photographer Manawatū, videographer Manawatū, Palmerston North photographer, Palmerston North videographer, Wellington photographer, Wellington videographer, commercial photography NZ, video production Wellington, brand photography, corporate video, social media content creator NZ";

export const BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": SITE_NAME,
  "url": SITE_URL,
  "email": "create@obracreatives.co.nz",
  "image": DEFAULT_IMAGE,
  "logo": `${SITE_URL}/oc-logo.png`,
  "description": DEFAULT_DESC,
  "address": { "@type": "PostalAddress", "addressRegion": "Manawatū-Whanganui", "addressCountry": "NZ" },
  "areaServed": [
    { "@type": "City", "name": "Palmerston North" },
    { "@type": "AdministrativeArea", "name": "Manawatū" },
    { "@type": "City", "name": "Wellington" },
    { "@type": "Country", "name": "New Zealand" }
  ],
  "knowsAbout": ["Commercial Photography", "Videography", "Video Production", "Brand Photography", "Corporate Video", "Social Media Content"],
  "makesOffer": ["Commercial Photography", "Commercial Videography", "Social Media Content"].map((name) => ({
    "@type": "Offer", "itemOffered": { "@type": "Service", "name": name, "areaServed": ["Manawatū", "Wellington"] }
  }))
};

const setMeta = (name, content, isProperty = false) => {
  const attr = isProperty ? "property" : "name";
  let meta = document.querySelector(`meta[${attr}="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attr, name);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
};

const setJsonLd = (id, data) => {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
};

export default function SEO({ title, description, path, image, type, schema }) {
  const pageTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const fullUrl = path ? SITE_URL + path : SITE_URL;
  const metaImage = image || DEFAULT_IMAGE;
  const metaDescription = description || DEFAULT_DESC;
  const pageType = type || "website";
  const schemaJson = schema ? JSON.stringify(schema) : "";

  useEffect(() => {
    document.title = pageTitle;
    setMeta("description", metaDescription);
    setMeta("keywords", KEYWORDS);
    setMeta("og:type", pageType, true);
    setMeta("og:url", fullUrl, true);
    setMeta("og:title", pageTitle, true);
    setMeta("og:description", metaDescription, true);
    setMeta("og:image", metaImage, true);
    setMeta("og:site_name", SITE_NAME, true);
    setMeta("og:locale", "en_NZ", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", pageTitle);
    setMeta("twitter:description", metaDescription);
    setMeta("twitter:image", metaImage);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", fullUrl);

    setJsonLd("seo-business", BUSINESS_SCHEMA);
    const pageSchema = document.getElementById("seo-page");
    if (schemaJson) setJsonLd("seo-page", JSON.parse(schemaJson));
    else if (pageSchema) pageSchema.remove();
  }, [pageTitle, metaDescription, fullUrl, metaImage, pageType, schemaJson]);

  return null;
}
